import { joinNames } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/jointAppointment'

// Every user-facing string of the Series tab and the card popover (verbatim from the spec).
// One visit reads "Visit 3", a range "Visits 3–5".
const visitSpan = (from: number, to: number): string =>
  from >= to ? `Visit ${from}` : `Visits ${from}–${to}`

export const SERIES_TEXT_V2 = {
  // Mode switch
  modeSingle: 'Single visit',
  modeSeries: 'Series',
  modeNoteCreateSingle: 'One appointment',
  modeNoteCreateSeries: 'A course of visits booked together, each its own appointment',
  modeNoteEditSeries: 'This appointment is part of a series',
  modeNoteEditOrdinary: 'A single appointment',
  loadFailed: 'Series details are unavailable.',
  tryAgain: 'Try again',
  // Create head
  templateLabel: 'Series template',
  templateBuildHere: 'Build it here',
  templateMeta: (visits: number, cadence: string): string => `${visits} visits · every ${cadence}`,
  visitsLabel: 'Number of visits',
  cadenceLabel: 'Cadence',
  every: 'Every',
  nameLabel: 'Series name',
  namePlaceholder: 'e.g. Vaccin Antigripal',
  noteLabel: 'Series note',
  editNoteLabel: 'Note',
  notePlaceholder: 'Shown on every visit of the series',
  problemLabel: 'For problem',
  problemNone: 'Not stated',
  decrease: 'Decrease',
  increase: 'Increase',
  unitDay: 'day(s)',
  unitWeek: 'week(s)',
  unitMonth: 'month(s)',
  // Schedule
  scheduleLabel: 'Visit schedule',
  colVisit: 'Visit',
  colName: 'Name',
  colDate: 'Date',
  colTime: 'Time',
  colMin: 'Min',
  colProvider: 'Provider',
  colStatus: 'Status',
  colInterval: 'Interval',
  intervalStart: 'start',
  intervalAdded: 'added',
  noName: '—',
  removeVisit: 'Remove visit',
  helpCreate:
    'Each visit is booked at its own date and time, with the provider from “Appointment with”. Change any visit in its row.',
  choosePrompt: 'Choose a patient and a provider to build the schedule.',
  freshNote: 'Each visit is booked at its own date and time.',
  regenerate: 'Regenerate from cadence',
  addVisit: '+ Add visit',
  staleNote: (count: number, cadence: string): string =>
    `Number of visits or cadence changed: regenerate to rebuild ${count} visits, every ${cadence}. Hand edits are lost.`,
  // Footer
  kickerSeries: 'Series',
  newSeries: 'New series',
  footerTitleCreate: (name: string, count: number, first: string, last: string): string =>
    `${name} · ${count} visits · ${first} – ${last}`,
  footerEvery: (cadence: string): string => `Every ${cadence}`,
  footerStale: ' · schedule not regenerated',
  footerAttention: (count: number): string =>
    ` · ${count} ${count === 1 ? 'visit needs' : 'visits need'} attention`,
  saveCreate: (count: number): string => `Save ${count} visits`,
  save: 'Save',
  // Edit head
  pill: (step: number | null, total: number): string =>
    step === null ? `SERIES ${total}` : `SERIES ${step}/${total}`,
  pillSub: (step: number | null, total: number): string =>
    step === null ? `${total} visits` : `this appointment is visit ${step} of ${total}`,
  labelLabel: 'Label',
  startedLabel: 'Started',
  forProblems: 'For problems',
  applyRemaining: 'also apply to the remaining steps',
  applyHelp:
    'Applying replaces the problems the run carried before on those steps. A problem added to one step by hand is left alone. Steps already delivered keep what they had.',
  // Edit table
  helpEdit:
    'The highlighted row is the visit you opened. Adjust any visit individually before saving.',
  unbookedProvider: 'Unbooked · recall',
  unbookedNoRecall: 'nobody is being asked for this visit',
  statusRecall: 'Recall',
  dash: '—',
  // Shift and bands
  shiftPrefix: 'Shift this visit onward by 1',
  shiftNote: (from: number, to: number, sign: '+' | '−', n: number, unit: string): string =>
    `${visitSpan(from, to)} moved ${sign}${n} ${unit}`,
  addPrefix: 'Add',
  shiftEarlier: 'Shift earlier',
  shiftLater: 'Shift later',
  fewerExtra: 'Fewer extra visits',
  moreExtra: 'More extra visits',
  addSuffix: 'more visits to this series, unbooked, each with a recall',
  cancelSeries: 'Cancel this series on Save',
  cancelNote: '(the booked appointments are left alone)',
  lockedVisit: 'OpenEMR did not change this visit: it is locked.',
  // Edit footer
  footerOpened: (step: number): string => `Visit ${step} opened`,
  footerMoved: (from: number, to: number): string =>
    ` · ${visitSpan(from, to).toLowerCase()} moved`,
  footerAdded: (count: number): string => ` · ${count} unbooked added`,
  // Calendar popover
  popoverTitle: 'Recurring series',
  popoverClose: 'Close',
  popoverStep: (step: number, total: number): string => `Session ${step} of ${total}`,
  popoverSession: (step: number, total: number, ends: string): string =>
    `Session ${step} of ${total} · ends ${ends}`,
  popoverRange: (cadence: string, start: string, end: string): string =>
    `${cadence} · ${start}–${end}`,
  editThis: 'Edit this one',
  editSeries: 'Edit series',
  pillTitle: (step: number, total: number): string => `Series visit ${step} of ${total}`,
  pillTitleProtocol: (step: number, total: number): string => `Protocol visit ${step} of ${total}`,
  // Providers
  applyAll: 'Apply providers to all visits',
  applyAllHint: 'Sets every visit to the providers above, with the same primary.',
  ownProviders: (count: number, position: number): string =>
    count === 1
      ? `Visit ${position} has its own providers`
      : `${count} visits have their own providers`,
  providerSummary: (name: string, others: number): string =>
    others > 0 ? `${name} + ${others}` : name,
  rowProviders: (position: number): string => `Visit ${position} providers`,
  unknownProvider: 'Unknown provider',
  seriesRemoval: (items: { name: string; visits: number[] }[]): string => {
    const total = items.reduce((sum, item) => sum + item.visits.length, 0)
    const parts = items.map(
      (item) =>
        `${item.name} from ${item.visits.length === 1 ? 'visit' : 'visits'} ${item.visits.join(
          ', ',
        )}`,
    )
    return `${joinNames(parts)} will be taken off and their ${
      total === 1 ? 'appointment' : 'appointments'
    } deleted, in OpenEMR too. Do you still want to continue?`
  },
} as const
